import React, { useEffect, useState, useContext, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import '../styles/OrderConfirmation.css';
import { StoreContext } from '../context/StoreContext';
import { getOrderById, verifyStripePayment } from '../api/apiService';

const OrderConfirmation = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { clearUserCart } = useContext(StoreContext);

    const orderId = searchParams.get('orderId');
    const paymentSuccessStatus = searchParams.get('success'); // 'true' or 'false' from Stripe/Razorpay redirects
    const paymentModeFromURL = searchParams.get('payment'); // 'cod', 'stripe', 'razorpay' from our Checkout.jsx navigate calls

    const isUserLoggedIn = localStorage.getItem('customer_token') !== null;

    // Use a ref to ensure verifyPayment only runs once per component mount for Stripe
    const verificationAttempted = useRef(false);

    // Effect for initial order fetch
    useEffect(() => {
        if (!orderId) {
            setError('No order ID found.');
            setLoading(false);
            return;
        }

        const fetchOrder = async () => {
    try {
        setLoading(true);
        const res = await getOrderById(orderId); // Use the imported function
        if (res.data.success && res.data.data) {
            setOrder(res.data.data);
            const orderData = res.data.data;

            if (orderData.guestEmail) {
                const guestOrderIds = JSON.parse(sessionStorage.getItem('guestOrderIds') || '[]');
                if (!guestOrderIds.includes(orderData._id)) {
                    guestOrderIds.push(orderData._id);
                    sessionStorage.setItem('guestOrderIds', JSON.stringify(guestOrderIds));
                }
            }
        } else {
            setError('Order not found.');
        }
    } catch (err) {
        console.error("Error fetching order in confirmation:", err.response?.data?.message || err.message);
        if (err.response?.status === 401 || err.response?.status === 403) {
            setError('You are not authorized to view this order or it does not exist.');
        } else {
            setError('Failed to fetch order details. Please try refreshing.');
        }
    } finally {
        setLoading(false);
    }
};
        fetchOrder();
    }, [orderId]);


    // Effect for Stripe payment verification and cart clearing (only for Stripe redirects)
// Effect for Stripe payment verification and cart clearing
useEffect(() => {
    // This effect is specifically for handling redirects back from Stripe
    if (
        order &&
        order.payment_mode === 'Stripe' &&
        order.payment_status !== 'paid' &&
        !verificationAttempted.current &&
        paymentModeFromURL === null
    ) {
        verificationAttempted.current = true; // Mark as attempted

        // Directly call the imported API function, breaking the infinite loop
        verifyStripePayment(order._id, paymentSuccessStatus)
            .then(res => {
                if (res.data.success) {
                    // If backend confirms success, refetch the order to get the 'paid' status
                    return getOrderById(order._id);
                } else {
                    // Throw an error to be caught by the .catch block
                    throw new Error(res.data.message || 'Stripe payment could not be verified.');
                }
            })
            .then(updatedOrderRes => {
                // This runs only if the refetch was successful
                if (updatedOrderRes && updatedOrderRes.data.success) {
                    setOrder(updatedOrderRes.data.data);
                }
            })
            .catch(err => {
                console.error("Error during Stripe verification:", err.message);
                setError('An error occurred during payment verification. Please contact support.');
            });
    }

    // --- Consolidated Cart Clearing Logic for All Online Payments ---
    if (
        order &&
        // order.payment_mode !== 'COD' &&   // COD support disabled — commented out
        order.payment_status === 'paid'
    ) {
        const clearedOrders = JSON.parse(sessionStorage.getItem('clearedOrders') || '[]');
        if (!clearedOrders.includes(order._id)) {
            clearUserCart();
            clearedOrders.push(order._id);
            sessionStorage.setItem('clearedOrders', JSON.stringify(clearedOrders));
        }
    }

}, [order, paymentSuccessStatus, clearUserCart, paymentModeFromURL]);


    if (loading) {
        return <div className="order-confirmation-page loading-state"><p>Loading your order...</p></div>;
    }
    if (error) {
        return <div className="order-confirmation-page error-state"><p>{error}</p></div>;
    }
    if (!order) {
        return null;
    }

    // --- DYNAMIC PAYMENT METHOD DISPLAY LOGIC ---
    let paymentMethodDisplay = '';
    let confirmationMessage = "Order Status Update"; // Default general message
    let additionalInfo = "";

    // ADDED: Define the currency symbol from the order object with a fallback.
    const currencySymbol = order?.currencySymbol || '₹';
    const formatPrice = (amount) => {
        const formattedAmount = Number(amount).toFixed(2);
        if (order?.currency === 'CAD') {
            return `${formattedAmount} ${currencySymbol}`;
        }
        return `${currencySymbol}${formattedAmount}`;
    };

    if (order.payment_mode === 'COD') {
        /*
        // COD handling disabled — commented out
        paymentMethodDisplay = 'Cash on Delivery';
        confirmationMessage = "Order Placed Successfully!";
        additionalInfo = `Payment method: Cash on Delivery. Please keep ${formatPrice(order.net_amount)} ready.`;
        */
    } 
    // This new block handles both Stripe and Razorpay generically
    else if (order.payment_mode === 'Stripe' || order.payment_mode === 'Razorpay') {
        paymentMethodDisplay = 'Online Payment';
        if (order.payment_status === 'paid') {
            confirmationMessage = "Payment Successful & Order Placed!";
            additionalInfo = `Your payment of ${formatPrice(order.net_amount)} was successful.`;
        } else if (order.status === 'payment_failed') {
            confirmationMessage = "Online Payment Failed!";
            additionalInfo = `Your online payment for ${formatPrice(order.net_amount)} could not be processed. Please try again or choose COD.`;
        } else {
            confirmationMessage = "Order Status Pending";
            additionalInfo = `Your order is awaiting payment confirmation. If already paid, please allow a moment for the status to update.`;
        }
    } else {
        paymentMethodDisplay = order.payment_mode || 'Unknown Payment Mode';
        confirmationMessage = "Order Status Pending";
        additionalInfo = "Please contact support for details.";
    }

    // COD disabled: normalize any remaining COD value to a generic online label
    if (order.payment_mode === 'COD') {
        paymentMethodDisplay = 'Online Payment';
    }

    // Determine the header class for styling (success, failure, pending)
    let headerClass = 'pending-header'; // Default
    if (order.payment_status === 'paid') {
        headerClass = 'success-header';
    } else if (order.status === 'payment_failed') {
        headerClass = 'failure-header';
    }
    // --- END DYNAMIC PAYMENT METHOD DISPLAY LOGIC ---


    return (
        <div className="order-confirmation-page order-success-state">
            <h2 className={headerClass}>
                {confirmationMessage}
            </h2>
            <p>Thank you for your order. Your order ID is: <strong>{order._id}</strong>.</p>
            <p>{additionalInfo}</p>
            <p>Payment Mode: <strong>{paymentMethodDisplay}</strong></p>

            {/*
                ✅ FIX: Prioritize specific delivery email, then guest, then account
            */}
            {order.delivery_address_snapshot?.email || order.guestEmail || order.user_id?.email ? (
                <p>
                    We will send a confirmation to <strong>
                        {order.delivery_address_snapshot?.email || order.guestEmail || order.user_id?.email || 'your email'}
                    </strong>.
                </p>
            ) : null}
            <div className="order-success-actions">
                {isUserLoggedIn ? (
                    <button onClick={() => navigate('/my-orders')} className="place-order-button">View My Orders</button>
                ) : (
                    // For guests, provide a direct link to track this specific order
                    <Link to={`/track-order/${order._id}`} className="place-order-button">
                        Track My Order
                    </Link>
                )}
                <button onClick={() => navigate('/')} className="place-order-button secondary">Continue Shopping</button>
            </div>
        </div>
    );
};

export default OrderConfirmation;
